import { Suspense, lazy, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';

import { FileX } from 'lucide-react';

import { cn } from '@/lib/utils';

import { ExcelPreviewPane } from '@/components/ExcelPreviewPane';
import { Skeleton } from '@/components/ui/skeleton';
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from '@/components/ui/resizable';

import { useDocumentPreviewUrl } from '@/features/DocumentSearch/api';

import type { GroundTruthPageRange } from './GroundTruthPdfViewer';
import type { HubPdfBoundary, HubPdfJump } from './HubPdfViewer';
import { FocusModeButton } from './FocusModeButton';
import { LoadError } from './LoadError';
import { PlaceholderCard } from './PlaceholderCard';
import { type TypeLabeller, bandLabel, bandsFromSaved } from './splitBands';
import type { SplitBand } from './splitBands';
import type { GtFileDoc, GtPageSplit } from './types';

// Lazy so pdf.js only loads once a PDF is first shown.
const GroundTruthPdfViewer = lazy(() =>
    import('./GroundTruthPdfViewer').then((m) => ({ default: m.GroundTruthPdfViewer })),
);

/** The original file with its saved split boundaries, offered next to the single-document preview. */
export interface WholeFilePreview {
    url: string;
    boundaries: HubPdfBoundary[];
    bands: SplitBand[];
    /** 1-based page the file opens at: the selected document's first page. */
    initialPage: number;
    /** 1-based pages of the selected document. */
    highlightRange: { first: number; last: number } | null;
}

type PreviewMode = 'doc' | 'file';

interface GroundTruthPreviewProps {
    /**
     * Document whose PDF preview should render on the left. Pass `null` when the
     * source has no rendered PDF (e.g. an Excel sheet) so the pane degrades to a
     * placeholder (or the Excel preview, if `excelPresignedUrl` is set) instead of
     * fetching/embedding nothing.
     */
    docId: number | null;
    /** Ingested file whose pages the viewer's find box searches. */
    ingestedFileId?: number | null;
    /**
     * Presigned URL for an Excel source. Only consulted when `docId` is `null`;
     * ignored for PDF sources, which always render via `docId`.
     */
    excelPresignedUrl?: string | null;
    /**
     * Presigned URL for the original PDF, shown when the file has no documents to
     * preview (e.g. its ingestion failed). Only consulted when `docId` is `null`.
     */
    originalFileUrl?: string | null;
    /** 0-based inclusive pages the document covers in its file; shown under the viewer. */
    pageRange?: GroundTruthPageRange | null;
    /** When set, a "This document" / "Whole file" toggle appears over the PDF preview. */
    wholeFile?: WholeFilePreview | null;
    /** Which view the PDF preview opens in; split surfaces pass `'file'`. */
    defaultMode?: PreviewMode;
    /** Scrolls the whole-file preview to a 1-based file page (and switches to it); needs `wholeFile`. */
    jumpTo?: HubPdfJump | null;
    /** Fires with the 1-based page of the whole file now in view. */
    onVisiblePageChange?: (page: number) => void;
    /** Called with the workbook's sheet names once the Excel preview loads (an empty list if it fails). */
    onExcelSheetNames?: (sheetNames: string[]) => void;
    /** Shows only this workbook sheet (0-based position) and drops the preview's own tab bar; pair it with `excelTabs`. */
    excelSheetIndex?: number | null;
    /** Rendered under the workbook in place of the preview's own sheet tabs. */
    excelTabs?: ReactNode;
    /** Initial share of the width given to the PDF preview, in percent; used by the resizable layout. */
    previewDefaultSize?: number;
    /** `resizable` (default) puts a drag handle between the panes; `columns` sizes the viewer to take what the editor column leaves. */
    layout?: 'resizable' | 'columns';
    /** Sizing for the editor column in the `columns` layout. */
    asideClassName?: string;
    /** Receives scroll events from anything scrolling inside the viewer pane. */
    onViewerScroll?: React.UIEventHandler<HTMLElement>;
    /** The editor pane rendered on the right half. */
    children: React.ReactNode;
    className?: string;
}

/** Preview share for split editors: narrower windows give the editor more room. */
export function splitEditorPreviewSize(): number {
    return window.matchMedia('(max-width: 1439px)').matches ? 45 : 50;
}

/** A doc's page range, or `null` when it has none. */
export function docPageRange(doc: GtFileDoc | undefined): GroundTruthPageRange | null {
    return doc?.startPage != null && doc.endPage != null
        ? { startPage: doc.startPage, endPage: doc.endPage }
        : null;
}

/** The whole-file preview for a PDF: splits (the editor's unsaved ones when given, else saved) become labelled bands, the selected doc's pages are ringed. */
export function wholeFilePreview(
    url: string,
    splits: GtPageSplit[],
    range: GroundTruthPageRange | null,
    liveBands: SplitBand[] | null = null,
    typeLabel?: TypeLabeller,
): WholeFilePreview {
    const bands = liveBands ?? bandsFromSaved(splits, typeLabel);
    return {
        url,
        bands,
        boundaries: bands.map((band) => ({ page: band.first, label: bandLabel(band) })),
        initialPage: range ? range.startPage + 1 : 1,
        highlightRange: range ? { first: range.startPage + 1, last: range.endPage + 1 } : null,
    };
}

/**
 * Two-column shell — PDF/Excel preview on the left, editor on the right.
 * Degrades gracefully when there is no PDF and no Excel URL (a placeholder
 * shows instead).
 *
 * PDFs render in the Hub's own pdf.js viewer.
 */
export function GroundTruthPreview({
    docId,
    ingestedFileId = null,
    excelPresignedUrl = null,
    originalFileUrl = null,
    pageRange = null,
    wholeFile = null,
    defaultMode = 'doc',
    jumpTo = null,
    onVisiblePageChange,
    onExcelSheetNames,
    excelSheetIndex = null,
    excelTabs = null,
    previewDefaultSize = 50,
    layout = 'resizable',
    asideClassName,
    onViewerScroll,
    children,
    className,
}: GroundTruthPreviewProps) {
    if (layout === 'columns') {
        return (
            <div
                className={cn('flex flex-1 min-h-0 min-w-0', className)}
                data-testid="GroundTruthPreview"
            >
                <section
                    className="h-full min-h-0 min-w-0 flex-[999_3_560px] overflow-hidden border-r border-[#E2F0F0] bg-[#F4FCFC]"
                    onScrollCapture={onViewerScroll}
                >
                    <PreviewPane
                        docId={docId}
                        ingestedFileId={ingestedFileId}
                        excelPresignedUrl={excelPresignedUrl}
                        originalFileUrl={originalFileUrl}
                        pageRange={pageRange}
                        wholeFile={wholeFile}
                        defaultMode={defaultMode}
                        jumpTo={jumpTo}
                        onVisiblePageChange={onVisiblePageChange}
                        onExcelSheetNames={onExcelSheetNames}
                        excelSheetIndex={excelSheetIndex}
                        excelTabs={excelTabs}
                    />
                </section>
                <aside
                    className={cn(
                        'flex h-full min-h-0 min-w-[360px] flex-[1_1_440px] flex-col max-w-[540px]',
                        asideClassName,
                    )}
                >
                    {children}
                </aside>
            </div>
        );
    }
    return (
        <div
            className={cn('flex flex-1 min-h-0 min-w-0', className)}
            data-testid="GroundTruthPreview"
        >
            <ResizablePanelGroup orientation="horizontal">
                <ResizablePanel defaultSize={`${previewDefaultSize}%`} minSize="20%" maxSize="80%">
                    <div
                        className="h-full min-h-0 min-w-0 overflow-hidden border-r bg-muted/30"
                        onScrollCapture={onViewerScroll}
                    >
                        <PreviewPane
                            docId={docId}
                            ingestedFileId={ingestedFileId}
                            excelPresignedUrl={excelPresignedUrl}
                            originalFileUrl={originalFileUrl}
                            pageRange={pageRange}
                            wholeFile={wholeFile}
                            defaultMode={defaultMode}
                            jumpTo={jumpTo}
                            onVisiblePageChange={onVisiblePageChange}
                            onExcelSheetNames={onExcelSheetNames}
                            excelSheetIndex={excelSheetIndex}
                            excelTabs={excelTabs}
                        />
                    </div>
                </ResizablePanel>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={`${100 - previewDefaultSize}%`} minSize="20%" maxSize="80%">
                    <div className="flex h-full flex-col min-h-0 min-w-0">
                        {children}
                    </div>
                </ResizablePanel>
            </ResizablePanelGroup>
        </div>
    );
}

function PreviewPane({
    docId,
    ingestedFileId,
    excelPresignedUrl,
    originalFileUrl,
    pageRange,
    wholeFile,
    defaultMode,
    jumpTo,
    onVisiblePageChange,
    onExcelSheetNames,
    excelSheetIndex,
    excelTabs,
}: {
    docId: number | null;
    ingestedFileId: number | null;
    excelPresignedUrl: string | null;
    originalFileUrl: string | null;
    pageRange: GroundTruthPageRange | null;
    wholeFile: WholeFilePreview | null;
    defaultMode: PreviewMode;
    jumpTo: HubPdfJump | null;
    onVisiblePageChange?: (page: number) => void;
    onExcelSheetNames?: (sheetNames: string[]) => void;
    excelSheetIndex: number | null;
    excelTabs: ReactNode;
}) {
    // Callers should pass exactly one of docId/excelPresignedUrl; if both are set,
    // Excel wins — PDF callers always pass excelPresignedUrl as null.
    if (docId === null && excelPresignedUrl !== null) {
        const pane = (
            <ExcelPreviewPane
                presignedUrl={excelPresignedUrl}
                showCellReferences
                alwaysShowTabs
                sheetIndex={excelSheetIndex}
                onLoad={(_count, names) => onExcelSheetNames?.(names)}
                onLoadError={() => onExcelSheetNames?.([])}
                loadingTestId="GroundTruthPreview__loading"
                tableTestId="GroundTruthPreview__excelTable"
                renderFallback={() => (
                    <PlaceholderCard
                        className="flex flex-col items-center justify-center h-full gap-2 px-6 text-center text-sm text-slate-600"
                        data-testid="GroundTruthPreview__unavailable"
                        icon={<FileX className="h-8 w-8 opacity-40" />}
                        title="Preview unavailable for this file."
                    />
                )}
            />
        );
        if (excelTabs === null) return pane;
        return (
            <div className="flex h-full min-h-0 flex-col">
                <div className="min-h-0 flex-1">{pane}</div>
                {excelTabs}
            </div>
        );
    }

    if (docId === null && originalFileUrl !== null) {
        return (
            <OriginalFilePane
                originalFileUrl={originalFileUrl}
                ingestedFileId={ingestedFileId}
                wholeFile={wholeFile}
                jumpTo={jumpTo}
                onVisiblePageChange={onVisiblePageChange}
            />
        );
    }

    if (docId === null) {
        return (
            <PlaceholderCard
                className="flex flex-col items-center justify-center h-full gap-2 px-6 text-center text-sm text-slate-600"
                data-testid="GroundTruthPreview__noPdf"
                icon={<FileX className="h-8 w-8 opacity-40" />}
                title="No PDF preview for this source."
            />
        );
    }

    return (
        <DocPreview
            docId={docId}
            ingestedFileId={ingestedFileId}
            pageRange={pageRange}
            wholeFile={wholeFile}
            defaultMode={defaultMode}
            jumpTo={jumpTo}
            onVisiblePageChange={onVisiblePageChange}
        />
    );
}

function OriginalFilePane({
    originalFileUrl,
    ingestedFileId,
    wholeFile,
    jumpTo,
    onVisiblePageChange,
}: {
    originalFileUrl: string;
    ingestedFileId: number | null;
    wholeFile: WholeFilePreview | null;
    jumpTo: HubPdfJump | null;
    onVisiblePageChange?: (page: number) => void;
}) {
    return (
        <PdfViewer
            file={originalFileUrl}
            ingestedFileId={ingestedFileId}
            boundaries={wholeFile?.boundaries}
            bands={wholeFile?.bands}
            toolbarLeading={<FocusModeButton />}
            jumpTo={jumpTo}
            onVisiblePageChange={onVisiblePageChange}
        />
    );
}

function DocPreview({
    docId,
    ingestedFileId,
    pageRange,
    wholeFile,
    defaultMode,
    jumpTo,
    onVisiblePageChange,
}: {
    docId: number;
    ingestedFileId: number | null;
    pageRange: GroundTruthPageRange | null;
    wholeFile: WholeFilePreview | null;
    defaultMode: PreviewMode;
    jumpTo: HubPdfJump | null;
    onVisiblePageChange?: (page: number) => void;
}) {
    const [mode, setMode] = useState<PreviewMode>(defaultMode);
    // A jump issued while mounted shows the whole file; the toggle clears it so a later switch opens at the document.
    const [seenSeq, setSeenSeq] = useState(jumpTo?.seq);
    const [activeJump, setActiveJump] = useState<HubPdfJump | null>(null);
    if (jumpTo?.seq !== seenSeq) {
        setSeenSeq(jumpTo?.seq);
        setActiveJump(jumpTo);
        if (jumpTo) setMode('file');
    }

    if (!wholeFile)
        return (
            <DocPdfPane
                docId={docId}
                ingestedFileId={ingestedFileId}
                pageRange={pageRange}
                toolbarLeading={<FocusModeButton />}
            />
        );

    const modeToggle = (
        <div
            role="group"
            aria-label="What the preview shows"
            className="inline-flex h-8 shrink-0 overflow-hidden rounded-lg border border-[#CBE1E1] text-sm"
            data-testid="GroundTruthPreview__modeToggle"
        >
            {(['doc', 'file'] as const).map((value) => (
                <button
                    key={value}
                    type="button"
                    aria-pressed={mode === value}
                    aria-label={value === 'doc' ? 'This document' : 'Whole file'}
                    title={value === 'doc' ? 'This document' : 'Whole file'}
                    className={cn(
                        'whitespace-nowrap px-3 hover:bg-[#EFF9F9]',
                        mode === value && 'bg-[#EFF9F9] font-medium',
                    )}
                    onClick={() => {
                        setMode(value);
                        setActiveJump(null);
                    }}
                >
                    <span className="@[640px]/tb:hidden">{value === 'doc' ? 'Doc' : 'File'}</span>
                    <span className="hidden @[640px]/tb:inline">
                        {value === 'doc' ? 'This document' : 'Whole file'}
                    </span>
                </button>
            ))}
        </div>
    );

    const toolbarLeading = (
        <>
            <FocusModeButton />
            {modeToggle}
        </>
    );

    return mode === 'doc' ? (
        <DocPdfPane
            docId={docId}
            ingestedFileId={ingestedFileId}
            pageRange={pageRange}
            toolbarLeading={toolbarLeading}
        />
    ) : (
        <PdfViewer
            file={wholeFile.url}
            docId={docId}
            ingestedFileId={ingestedFileId}
            initialPage={wholeFile.initialPage}
            highlightRange={wholeFile.highlightRange}
            boundaries={wholeFile.boundaries}
            bands={wholeFile.bands}
            toolbarLeading={toolbarLeading}
            jumpTo={activeJump}
            onVisiblePageChange={onVisiblePageChange}
        />
    );
}

function PdfViewer(props: ComponentProps<typeof GroundTruthPdfViewer>) {
    return (
        <Suspense fallback={<LoadingPreview />}>
            <GroundTruthPdfViewer {...props} />
        </Suspense>
    );
}

function LoadingPreview() {
    return (
        <div
            role="status"
            aria-busy="true"
            aria-label="Loading preview"
            className="h-full p-4"
            data-testid="GroundTruthPreview__loading"
        >
            <Skeleton className="h-full w-full" />
        </div>
    );
}

function DocPdfPane({
    docId,
    ingestedFileId,
    pageRange,
    toolbarLeading,
}: {
    docId: number;
    ingestedFileId: number | null;
    pageRange: GroundTruthPageRange | null;
    toolbarLeading?: React.ReactNode;
}) {
    const { url, isLoading, isError, refetch, isRefetching } =
        useDocumentPreviewUrl(docId);
    const [previewPageCount, setPreviewPageCount] = useState<number>();

    if (isLoading) return <LoadingPreview />;

    if (isError && !url) {
        return (
            <div className="p-4" data-testid="GroundTruthPreview__error">
                <LoadError
                    what="the preview"
                    onRetry={() => void refetch()}
                    isRetrying={isRefetching}
                />
            </div>
        );
    }

    if (!url) {
        return (
            <PlaceholderCard
                className="flex flex-col items-center justify-center h-full gap-2 px-6 text-center text-sm text-slate-600"
                data-testid="GroundTruthPreview__unavailable"
                icon={<FileX className="h-8 w-8 opacity-40" />}
                title="Preview unavailable for this file."
            />
        );
    }

    // The preview is the doc's own pages only when the backend could slice it; a doc whose
    // range it can't read gets the whole file, so open at the range and ring it instead.
    const isWholeFile =
        pageRange !== null &&
        previewPageCount !== undefined &&
        previewPageCount !== pageRange.endPage - pageRange.startPage + 1;

    return (
        <PdfViewer
            file={url}
            docId={docId}
            ingestedFileId={ingestedFileId}
            pageOffset={isWholeFile ? 0 : (pageRange?.startPage ?? 0)}
            initialPage={isWholeFile ? pageRange.startPage + 1 : 1}
            highlightRange={
                isWholeFile
                    ? { first: pageRange.startPage + 1, last: pageRange.endPage + 1 }
                    : null
            }
            toolbarLeading={toolbarLeading}
            onPageCount={setPreviewPageCount}
        />
    );
}
